import { Dialog, Transition } from "@headlessui/react";
import { FC, Fragment } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Textfit } from "react-textfit";
import { useData } from "@providers/DataContext";
import { NLUserMailState } from "@models/Models";
import { isHiddenFeedSender } from "@utils/UserMailState+Visibility";
import { ReactComponent as CrossEyeIcon } from "@images/cross_eye.svg";
import { ReactComponent as ExitIcon } from "@images/exit_icon.svg";
import { ReactComponent as OptionsIcon } from "@images/options_dots.svg";
import IconButton from "@components/common/IconButton";
import Dropdown, { DropdownItem } from "@components/common/Dropdown";

interface HiddenSendersModalProps {
	isShow: boolean;
	onClose: () => void;
	onRestoreSender: (userMailState: NLUserMailState) => void;
}

const HiddenSendersModal: FC<HiddenSendersModalProps> = ({ isShow, onClose, onRestoreSender }) => {
	const { userMailStates } = useData();
	const hiddenSenders = (userMailStates ?? [])
		.filter(isHiddenFeedSender)
		.sort((a, b) => (a.sender_name ?? "").localeCompare(b.sender_name ?? ""));

	useHotkeys("Escape", () => onClose());

	const getSenderOptions = (userMailState: NLUserMailState): DropdownItem[] => {
		return [{
			name: "Show in Senders",
			icon: "eye_icon",
			action: () => onRestoreSender(userMailState),
		}];
	};

	return (
		<Transition as={Fragment} show={isShow}>
			<Dialog onClose={onClose} className="relative z-40">
				<Transition.Child as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
					<div className="fixed inset-0 bg-black bg-opacity-70" aria-hidden="true" />
				</Transition.Child>
				<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
					<Transition.Child className="w-full h-full" enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
						<Dialog.Panel className="m-auto w-full h-full max-w-lg transform overflow-hidden rounded-2xl bg-secondary border border-primary-200 text-left align-middle shadow-xl transition-all">
							<div className="absolute w-full bg-surface backdrop-blur-md top-0 left-0 flex flex-row justify-between items-center p-4 pb-3 border-b border-primary-200 z-20">
								<Dialog.Title className="text-primary font-medium font-primary text-xl">Hidden senders</Dialog.Title>
								<IconButton Icon={ExitIcon} className="-mr-2.5" onClick={onClose} />
							</div>
							{hiddenSenders.length > 0 ? (
								<div className="flex h-full flex-col gap-3 overflow-auto bg-secondary p-4 pt-[105px]">
									{hiddenSenders.map((sender) => (
										<div key={sender.id} className="flex h-[60px] w-full items-center gap-4 rounded-xl border border-primary-100 bg-surface p-3 shadow-sm">
											<div className="flex min-w-0 flex-grow">
												<Textfit min={12} max={16} mode="single" className="truncate text-left font-primary text-base font-medium text-primary">
													{sender.sender_name}
												</Textfit>
											</div>
											<Dropdown buttonOnEdge={true} buttonIcon={OptionsIcon} items={getSenderOptions(sender)} />
										</div>
									))}
								</div>
							) : (
								<div className="flex h-full flex-col items-center justify-center gap-3 px-6 pt-[70px] text-center">
									<CrossEyeIcon className="h-[42px] w-auto fill-primary-500 opacity-40" />
									<div className="font-primary text-base font-black text-primary">No hidden senders</div>
									<div className="max-w-sm font-primary text-sm font-regular text-primary-500">From Senders, open the three-dot menu beside a newsletter and choose Hide from Senders.</div>
								</div>
							)}
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
};

export default HiddenSendersModal;
